import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "@/components/product/ProductCard";
import EmptyState from "@/components/common/EmptyState";
import Rating from "@/components/common/Rating";
import { getProducts } from "@/services/catalog";
import { useI18n } from "@/i18n/I18nProvider";

const SORTS = ["relevance", "price_asc", "price_desc", "rating", "newest"];
const PER_PAGE = 24;

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-2xl border border-border">
          <div className="aspect-square shimmer" />
          <div className="space-y-2 p-3">
            <div className="h-3 w-3/4 rounded shimmer" />
            <div className="h-8 w-full rounded-full shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProductListing({ title, baseParams = {} }) {
  const { t } = useI18n();
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [applied, setApplied] = useState({ min: "", max: "" });
  const [inStockOnly, setInStockOnly] = useState(false);
  const [onSale, setOnSale] = useState(Boolean(baseParams.onSale));
  const [minRating, setMinRating] = useState(0);
  const [mobileFilters, setMobileFilters] = useState(false);

  const params = useMemo(
    () => ({
      ...baseParams,
      sort,
      page,
      perPage: PER_PAGE,
      minPrice: applied.min || undefined,
      maxPrice: applied.max || undefined,
      onSale: onSale || undefined,
    }),
    [baseParams, sort, page, applied, onSale]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["products", params],
    queryFn: () => getProducts(params),
  });

  const items = (data?.items || []).filter(
    (p) => (!inStockOnly || p.inStock) && (!minRating || p.rating >= minRating)
  );
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const applyPrice = () => { setApplied({ min: minPrice, max: maxPrice }); setPage(1); };
  const clearAll = () => {
    setMinPrice(""); setMaxPrice(""); setApplied({ min: "", max: "" });
    setInStockOnly(false); setOnSale(false); setMinRating(0); setPage(1);
  };

  const Filters = (
    <div className="space-y-6" data-testid="filters">
      <div>
        <h4 className="mb-3 text-sm font-bold text-ink">{t("price")}</h4>
        <div className="flex items-center gap-2">
          <input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} type="number" placeholder={t("min")} className="h-10 w-full rounded-lg border border-border px-3 text-sm outline-none focus:border-brand" data-testid="filter-min-price" />
          <span className="text-muted-foreground">—</span>
          <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} type="number" placeholder={t("max")} className="h-10 w-full rounded-lg border border-border px-3 text-sm outline-none focus:border-brand" data-testid="filter-max-price" />
        </div>
        <button onClick={applyPrice} className="mt-3 h-9 w-full rounded-full bg-ink text-sm font-semibold text-white hover:bg-brand">{t("apply")}</button>
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold text-ink">{t("availability")}</h4>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} className="h-4 w-4 accent-brand" data-testid="filter-instock" />
          {t("in_stock_only")}
        </label>
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={onSale} onChange={(e) => { setOnSale(e.target.checked); setPage(1); }} className="h-4 w-4 accent-brand" data-testid="filter-onsale" />
          {t("on_sale")}
        </label>
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold text-ink">{t("rating")}</h4>
        <div className="space-y-1.5">
          {[4, 3, 2].map((r) => (
            <button key={r} onClick={() => { setMinRating(minRating === r ? 0 : r); }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${minRating === r ? "bg-secondary" : "hover:bg-secondary/60"}`} data-testid={`filter-rating-${r}`}>
              <Rating rating={r} showCount={false} /> <span className="text-xs text-muted-foreground">& up</span>
            </button>
          ))}
        </div>
      </div>

      <button onClick={clearAll} className="text-sm font-semibold text-brand hover:underline" data-testid="clear-filters">{t("clear_all")}</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      {title && <h1 className="mb-1 font-display text-2xl font-bold text-ink sm:text-3xl">{title}</h1>}
      <p className="mb-5 text-sm text-muted-foreground">{total} {t("results")}</p>

      <div className="flex items-center justify-between gap-3 lg:hidden">
        <button onClick={() => setMobileFilters(true)} className="flex h-10 items-center gap-2 rounded-full border border-border px-4 text-sm font-semibold" data-testid="open-filters">
          <SlidersHorizontal size={16} /> {t("filters")}
        </button>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} className="h-10 rounded-full border border-border px-3 text-sm outline-none" data-testid="sort-select-mobile">
          {SORTS.map((s) => <option key={s} value={s}>{t(`sort_${s}`)}</option>)}
        </select>
      </div>

      <div className="mt-5 flex gap-6">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-28 rounded-2xl border border-border p-5">{Filters}</div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 hidden items-center justify-end lg:flex">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t("sort")}</span>
              <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} className="h-10 rounded-full border border-border px-3 text-sm outline-none" data-testid="sort-select">
                {SORTS.map((s) => <option key={s} value={s}>{t(`sort_${s}`)}</option>)}
              </select>
            </div>
          </div>

          {isLoading ? (
            <SkeletonGrid />
          ) : items.length === 0 ? (
            <EmptyState title={t("no_products_title")} body={t("no_products_body")} />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
              {items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="grid h-10 w-10 place-items-center rounded-full border border-border disabled:opacity-40"><ChevronLeft size={18} /></button>
              {Array.from({ length: totalPages }).slice(0, 7).map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)} className={`h-10 w-10 rounded-full text-sm font-semibold ${page === i + 1 ? "bg-ink text-white" : "border border-border"}`}>{i + 1}</button>
              ))}
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="grid h-10 w-10 place-items-center rounded-full border border-border disabled:opacity-40"><ChevronRight size={18} /></button>
            </div>
          )}
        </div>
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute inset-y-0 start-0 w-80 max-w-[85%] overflow-y-auto bg-background p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">{t("filters")}</h3>
              <button onClick={() => setMobileFilters(false)}><X /></button>
            </div>
            {Filters}
          </div>
        </div>
      )}
    </div>
  );
}
